import { Button } from "@/components/ui/button";
import { Phone, MessageCircle, ArrowDown, ExternalLink, ShieldCheck, Clock, Award } from "lucide-react";

import logoPsicoterapias from "@/assets/logo_psicoterapias.webp";
import ScrollReveal from "@/components/shared/ScrollReveal";

const HeroSection = () => {
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Background image with gradient overlay */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
          src="/hero-video.mp4"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-background/50 to-background/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/50 to-transparent" />
      </div>

      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden z-[1]">
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-primary/5 blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-equilybra-teal/5 blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <ScrollReveal variant="fade-right" duration={800}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
                <ShieldCheck className="w-4 h-4" />
                NR-1 já em vigor · fiscalização ativa do MTE
              </div>
            </ScrollReveal>

            {/* LCP: renderizado imediatamente, sem wrapper de reveal, para evitar atraso de pintura */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight">
              <span className="block text-lg md:text-xl font-semibold text-primary mb-2 tracking-wide uppercase">
                Equilybra Company
              </span>
              Consultoria em Saúde Mental{" "}
              <span className="text-gradient">Corporativa</span>
              <br />para{" "}
              <span className="text-gradient">Empresas</span> em São Paulo
              <span className="sr-only"> — Adequação à NR-1, Laudos Psicológicos e Psicólogo In Company</span>
            </h1>

            <ScrollReveal variant="fade-up" delay={200}>
              <h2 className="text-lg text-muted-foreground leading-relaxed max-w-xl font-normal">
                Sua empresa já está em conformidade com a NR-1? Ajudamos RH e lideranças a implementar DRPS, plano de ação e laudos psicológicos — evitando autuações, multas e impacto no FAP.
              </h2>
            </ScrollReveal>

            <ScrollReveal variant="fade-up" delay={350}>
              <div className="flex flex-wrap gap-4">
                <Button variant="hero" size="xl" asChild>
                  <a href="https://wa.me/5511976974905?text=Olá!%20Quero%20o%20diagnóstico%20gratuito%20de%20conformidade%20NR-1%20da%20Equilybra%20Company." target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-5 h-5" />
                    Diagnóstico NR-1 gratuito
                  </a>
                </Button>
                <Button variant="hero-outline" size="xl" asChild>
                  <a href="tel:1123050747">
                    <Phone className="w-5 h-5" />
                    Falar com especialista
                  </a>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Resposta em até 1 dia útil · sem compromisso
              </p>
            </ScrollReveal>

            <ScrollReveal variant="fade-up" delay={450}>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-4">
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" />
                  <span className="text-sm text-muted-foreground">Psicólogos registrados no CRP</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                  <span className="text-sm text-muted-foreground">Testes aprovados pelo CFP</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  <span className="text-sm text-muted-foreground">Presencial em SP · Online no Brasil</span>
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Floating CTA card */}
          <div className="relative hidden lg:flex flex-col justify-center items-center gap-6">
            <ScrollReveal variant="zoom" delay={500}>
              <a
                href="https://www.equilybra.com.br"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-card/80 backdrop-blur-sm rounded-2xl p-6 shadow-card border border-border max-w-sm animate-float hover:shadow-lg hover:scale-105 transition-all duration-300 group"
              >
                <div className="flex flex-col items-center gap-4 text-center">
                  <img
                    src={logoPsicoterapias}
                    alt="Equilybra Psicoterapias"
                    width={151}
                    height={80}
                    className="h-20 w-auto object-contain transition-[filter] duration-300 dark:[filter:drop-shadow(0_0_10px_hsl(var(--primary)/0.45))_drop-shadow(0_0_22px_hsl(0_0%_100%/0.15))]"
                  />
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Conheça nosso setor especializado em serviços para pessoas. Psicoterapias, Avaliações...
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:underline">
                    Saiba mais <ExternalLink className="w-3.5 h-3.5" />
                  </span>
                </div>
              </a>
            </ScrollReveal>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ArrowDown className="w-6 h-6 text-muted-foreground" />
      </div>
    </section>
  );
};

export default HeroSection;
